import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import InfiniteScroll from 'react-infinite-scroller';
import type { Content } from '@jwp/ott-common/types/config';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { mediaURL, slugify } from '@jwp/ott-common/src/utils/urlFormatting';
import { parseAspectRatio, parseTilesDelta } from '@jwp/ott-common/src/utils/collection';
import { testId } from '@jwp/ott-common/src/utils/common';
import { PersonalShelf, PLAYLIST_LIMIT_MAX, SHELF_LAYOUT_TYPE } from '@jwp/ott-common/src/constants';
import usePlaylists from '@jwp/ott-hooks-react/src/usePlaylists';
import { useTranslationKey } from '@jwp/ott-hooks-react/src/useTranslationKey';

import Shelf from '../../components/Shelf/Shelf';
import InfiniteScrollLoader from '../../components/InfiniteScrollLoader/InfiniteScrollLoader';
import ErrorPage from '../../components/ErrorPage/ErrorPage';
import Fade from '../../components/Animation/Fade/Fade';
import HeroShelf from '../../components/HeroShelf/HeroShelf';
import { getScrollParent } from '../../utils/dom';
import CardGrid from '../../components/CardGrid/CardGrid';

import styles from './ShelfList.module.scss';

const INITIAL_ROWS_TO_LOAD = 6;
const ROWS_TO_LOAD_STEP = 4;

type Props = {
  rows: Content[];
  forceHeroShelf?: boolean;
};

const ShelfList = ({ rows, forceHeroShelf }: Props) => {
  const { accessModel } = useConfigStore(({ accessModel }) => ({ accessModel }), shallow);
  const [rowsToLoad, setRowsToLoad] = useState(INITIAL_ROWS_TO_LOAD);
  const { t } = useTranslation('error');
  const translationKey = useTranslationKey('title');

  const watchHistoryDictionary = useWatchHistoryStore((state) => state.getDictionaryWithSeries());

  // User
  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);

  // Todo: move to more common package?
  const singlePlaylist = rows.length === 1;
  const playlists = usePlaylists(rows, rowsToLoad, singlePlaylist ? PLAYLIST_LIMIT_MAX : undefined);

  useEffect(() => {
    // reset row count when the page changes
    return () => setRowsToLoad(INITIAL_ROWS_TO_LOAD);
  }, [rows]);

  useEffect(() => {
    const allLoadedEmpty = playlists.slice(0, rowsToLoad).every(({ data, isSuccess }) => isSuccess && !data?.playlist?.length);

    if (allLoadedEmpty && rowsToLoad < rows.length) {
      setRowsToLoad((current) => Math.min(current + ROWS_TO_LOAD_STEP, rows.length));
    }
  }, [playlists, rows.length, rowsToLoad]);

  // If all playlists are empty (most probably due to geo restrictions), we show an empty shelves error
  const allPlaylistsEmpty = playlists.every(({ data, isSuccess }) => isSuccess && !data?.playlist?.length);

  if (allPlaylistsEmpty) {
    return <ErrorPage title={t('empty_shelves_heading')} message={t('empty_shelves_description')} />;
  }

  if (singlePlaylist) {
    if (!playlists[0].data) return;

    return (
      <section className={classNames(styles.shelfContainer, styles.grid)}>
        <CardGrid
          getUrl={(playlistItem) =>
            mediaURL({
              id: playlistItem.mediaid,
              title: playlistItem.title,
              playlistId: playlists[0].data?.feedid,
              // @CUSTOMER vd-ott-iq autoplay live channels
              play: playlistItem.mediaid.startsWith('channel'),
              // /@CUSTOMER
            })
          }
          playlist={playlists[0].data}
          accessModel={accessModel}
          isLoading={playlists[0].isPlaceholderData || false}
          isLoggedIn={!!user}
          hasSubscription={!!subscription}
        />
      </section>
    );
  }

  const scrollParent = getScrollParent();

  return (
    <div className={styles.shelfList}>
      <InfiniteScroll
        loadMore={() => setRowsToLoad((current) => Math.min(current + ROWS_TO_LOAD_STEP, rows.length))}
        hasMore={rowsToLoad < rows.length}
        loader={<InfiniteScrollLoader key="loader" />}
        getScrollParent={() => scrollParent}
        useWindow={scrollParent === document.documentElement || scrollParent === document.body}
      >
        {rows.slice(0, rowsToLoad).map(({ type, featured, title, custom }, index) => {
          const { data: playlist, isPlaceholderData, error } = playlists[index];

          if (!playlist?.playlist?.length) return null;

          const posterAspect = parseAspectRatio(playlist.cardImageAspectRatio || playlist.shelfImageAspectRatio);
          const visibleTilesDelta = parseTilesDelta(posterAspect);

          const translatedValue = custom?.[translationKey] || (playlist?.[translationKey] as string);
          const translatedTitle = translatedValue || title || playlist?.title;

          const isHero = (forceHeroShelf || custom?.layoutType === SHELF_LAYOUT_TYPE.hero) && index === 0;
          const isFeatured = !isHero && (custom?.layoutType === SHELF_LAYOUT_TYPE.featured || featured);

          return (
            <section
              key={`${index}_${playlist.id}`}
              className={classNames(styles.shelfContainer, { [styles.hero]: isHero, [styles.featured]: isFeatured })}
              data-testid={testId(`shelf-${isHero ? 'hero' : isFeatured ? 'featured' : type === 'playlist' ? slugify(translatedTitle) : type}`)}
              aria-label={translatedTitle}
            >
              {isHero ? (
                <HeroShelf loading={isPlaceholderData} error={error} playlist={playlist} />
              ) : (
                <Fade duration={250} delay={index * 33} open>
                  <Shelf
                    loading={isPlaceholderData}
                    error={error}
                    type={type}
                    playlist={playlist}
                    watchHistory={type === PersonalShelf.ContinueWatching ? watchHistoryDictionary : undefined}
                    title={translatedTitle}
                    featured={isFeatured}
                    accessModel={accessModel}
                    isLoggedIn={!!user}
                    hasSubscription={!!subscription}
                    posterAspect={posterAspect}
                    visibleTilesDelta={visibleTilesDelta}
                  />
                </Fade>
              )}
            </section>
          );
        })}
      </InfiniteScroll>
    </div>
  );
};

export default ShelfList;
